import type { ReactNode } from 'react';
import { IdControl } from '../components';
import type { ContainerKind } from '../components';
import { BackButton } from '../components';

/**
 * Operation-detail page header — back button, title + id + tag actions row,
 * optional action buttons (rerun, re-evaluate, playground, …).
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface DetailHeaderProps {
    title: string;
    id: string;
    /** Cosmos container name for IdControl's copy-to-clipboard tooltip. */
    idContainer?: ContainerKind;
    /** "Back" button label, e.g. "Back to Submissions". */
    backLabel: string;
    onBack: () => void;
    /** Rendered to the right of the IdControl — tag actions, badges, etc. */
    titleRight?: ReactNode;
    /** Rendered as a second row under the title, right-aligned. Used for
     *  rerun / re-evaluate / playground buttons. */
    actions?: ReactNode;
    /** Error/status line shown beneath the actions row. */
    actionStatus?: ReactNode;
}

export function DetailHeader({
    title,
    id,
    idContainer,
    backLabel,
    onBack,
    titleRight,
    actions,
    actionStatus,
}: DetailHeaderProps) {
    return (
        <div className="page-header">
            {/* Renders the shared BackButton, which honours the cross-page
             *  `returnTo` convention (shared-ui/navigation). When the page
             *  was reached via an internal link, the source's label is used
             *  and `navigate(-1)` pops history; otherwise the default
             *  label + handler below take effect. */}
            <BackButton label={backLabel} onClick={onBack} />
            <div className="header-content">
                <div className="header-title">
                    <h1>{title}</h1>
                    {/* Id then tags, on one row. The column layout used to stack them,
                     *  so the tags dropped to a line of their own under the id. */}
                    <div className="header-title-meta">
                        <IdControl value={id} container={idContainer} />
                        {titleRight}
                    </div>
                </div>
                {(actions || actionStatus) && (
                    <div className="header-actions-wrap">
                        {actions && <div className="header-actions">{actions}</div>}
                        {actionStatus}
                    </div>
                )}
            </div>
        </div>
    );
}
